import useMyRole from '@hooks/useMyRole';
import useAppointmentStatus from '@hooks/useAppointmentStatus';
import { useVideoCallSignallingType } from '@hooks/useVideoCallSignallingType';
import { useAppointmentRoomDT } from '@hooks/useTestPatientRoom';
import { useLayoutEffect, useState } from 'react';

type Appointment = any;

export type PartyStatus = 'connected' | 'disconnected' | 'in-session' | 'testing-webcam';
export type TwilioEventStatus = 'USRPOPUP' | 'USRWAIT' | 'USRCONN' | 'USRENDED' | 'CAEND' | 'CASTART';
export const patientStatusToTwilioEvent: Record<PartyStatus, TwilioEventStatus> = {
  // @ts-ignore
  none: '',
  upcoming: 'USRPOPUP',
  waiting: 'USRWAIT',
  'in-call': 'USRCONN',
  'call-over': 'USRENDED'
};
export const clinicianStatusToTwilioEvent: Record<PartyStatus, TwilioEventStatus> = {
  // @ts-ignore
  none: '',
  upcoming: 'USRPOPUP',
  waiting: 'USRWAIT',
  'in-call': 'CASTART',
  'call-over': 'CAEND'
};

/**
 * Returns the "realtime" status of the other party in an appointment.
 * Initially read from the backend and updated via the Twilio WebSocket.
 *
 * @param appointment
 * @param role
 * @returns other-party-status
 */
export function usePartyStatus(appointment: Appointment, role): PartyStatus {
  const { clinicianStatus, patientStatus } = useAppointmentStatus(appointment);
  const status = role === 'patient' ? patientStatus : clinicianStatus;

  return status.status;
}

function useOtherPartyStatusWS(appointment: Appointment): PartyStatus {
  const role = useMyRole();

  return usePartyStatus(appointment, role === 'clinician' ? 'patient' : 'clinician');
}

function useOtherPartyStatusDT(appointment: Appointment): PartyStatus {
  const { room } = useAppointmentRoomDT(appointment);
  const [connected, setConnected] = useState((room?.participants?.size || 0) > 0);

  useLayoutEffect(() => {
    if (!room) return () => {};

    const connected = () => setConnected(true);
    const disconnected = () => setConnected(false);

    room.on('participantConnected', () => {
      connected();
    });
    room.on('participantDisconnected', () => {
      disconnected();
    });

    setConnected((room?.participants?.size || 0) > 0);
    return () => {
      room.off('participantConnected', connected);
      room.off('participantDisconnected', disconnected);
    };
  }, [room]);

  return connected ? 'connected' : 'disconnected';
}

/* eslint-disable react-hooks/rules-of-hooks */
function useOtherPartyStatus(appointment: Appointment) {
  if (useVideoCallSignallingType(appointment) === 'ws') {
    return useOtherPartyStatusWS(appointment);
  }

  return useOtherPartyStatusDT(appointment);
}

export default useOtherPartyStatus;
